import React, { FunctionComponent, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import { LocalizationProvider, MessageWindow } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { IAppState } from "@multiweb/types/react";
import { createUpdateSetsAction } from "../../../actions";
import { LOC_CANCEL, LOC_CANNOT_DELETE_DISPLAYED_SET, LOC_DELETE, LOC_NEW_SET, LOC_OK, LOC_REALLY_DELETE_SET } from "../../../locKeys";
import { createGetOrderedSets, getSelectedSetIndex } from "../../../selectors";
import { ISet, MessageWindowState, WorkflowEditorProps } from "../../../types";
import { UiWorkflow } from "../../../UiWorkflow";
import { getUiWorkflowMutableObject } from "../../../utils";
import { SideListFrame, SidePanelItemsSource } from "../components";
import { EditDialog } from "../EditDialog";
import { SetEditPage } from "./tabs/pages";
import { saveSets } from "./utils";

const { generateUUID } = bridge;

type SetEditDialogComponentProps = {
    /** Sada aktuálně zobrazená plátnem picassa */
    readonly displayedSetIndex: number;
    readonly sets: readonly ISet[];
    readonly uiWorkflow: UiWorkflow;
    readonly updateSets: (newSets: readonly ISet[]) => void;
    readonly onCloseEdit: () => void;
    readonly newSetText: string;
} & WorkflowEditorProps;

const SetEditDialogComponent: FunctionComponent<SetEditDialogComponentProps> = ({ hostKey, displayedSetIndex, sets, uiWorkflow, updateSets, onCloseEdit, newSetText }) => {
    const [touched, setTouched] = useState(false);
    const [selectedSetIndex, setSelectedSetIndex] = useState(displayedSetIndex);
    const [messageWindowState, setMessageWindowState] = useState<MessageWindowState>({ isOpen: false, properties: null });
    const selectedSet = sets[selectedSetIndex]; // selectedSet je pro editační okno, displayedSet pro picasso (můžu editovat i nezobrazenou sadu)
    const canSave = sets.every(set => !!set.name);

    const setsItemsSource: readonly SidePanelItemsSource[] = sets.map(({ name, description }, index) => ({
        headerName: name === "" ? newSetText : name,
        description1: description,
        onItemClick: () => setSelectedSetIndex(index),
    }));

    const onCloseMessageWindow = () => {
        setMessageWindowState({ isOpen: false, properties: null });
    };

    const handleUpdateSets = (newSets: readonly ISet[]) => {
        updateSets(newSets);
        !touched && setTouched(true);
    };

    const updateSet = (newSet: ISet) => {
        const newSets = [...sets];
        newSets[selectedSetIndex] = newSet;
        handleUpdateSets(newSets);
    };

    const addSet = () => {
        const newSets = [...sets, {
            id: generateUUID(),
            name: "",
            description: "",
        }];
        handleUpdateSets(newSets);
        setSelectedSetIndex(newSets.length - 1);
    };

    const onRemoveSetClick = () => {
        if (selectedSetIndex === displayedSetIndex)
            setMessageWindowState({
                isOpen: true,
                properties: {
                    children: <LocalizationProvider locKey={LOC_CANNOT_DELETE_DISPLAYED_SET} />,
                    primaryButtonLocKey: LOC_OK,
                    onCloseMessageWindow,
                },
            });
        else
            setMessageWindowState({
                isOpen: true,
                properties: {
                    children: <LocalizationProvider locKey={LOC_REALLY_DELETE_SET} />,
                    primaryButtonLocKey: LOC_DELETE,
                    secondaryButtonLocKey: LOC_CANCEL,
                    onPrimaryButtonClick: removeSet,
                    onCloseMessageWindow,
                },
            });
    };

    const removeSet = () => {
        if (sets.length === 1 || selectedSetIndex === displayedSetIndex)
            return;

        const newSets = sets.filter((_set, setIndex) => setIndex !== selectedSetIndex);
        handleUpdateSets(newSets);
        setSelectedSetIndex(selectedSetIndex - 1 >= 0 ? selectedSetIndex - 1 : 0);
    };

    const copySet = () => {
        const newSets = [...sets];
        newSets.splice(selectedSetIndex + 1, 0, { ...selectedSet, id: generateUUID() });
        handleUpdateSets(newSets);
        setSelectedSetIndex(selectedSetIndex + 1);
    };

    const changeSelectedSetOrder = (newIndex: number) => {
        const newSets = [...sets];
        newSets[selectedSetIndex] = newSets[newIndex];
        newSets[newIndex] = selectedSet;
        handleUpdateSets(newSets);
        setSelectedSetIndex(newIndex);
    };

    const onSaveClick = async () => {
        saveSets(sets, uiWorkflow.workflow);
    };

    const onCancelClick = () => {
        const { sets } = uiWorkflow.workflowModel;
        updateSets(sets);
    };

    return (
        <EditDialog width={900} height={700} {...{ hostKey, touched, canSave, onSaveClick, onCloseEdit, onCancelClick, setTouched }}>
            <SideListFrame
                hostKey={hostKey}
                selectedItemIndex={selectedSetIndex}
                itemsSource={setsItemsSource}
                onAddItemClick={addSet}
                onRemoveItemClick={onRemoveSetClick}
                onCopyItemClick={copySet}
                onOrderChange={changeSelectedSetOrder}>
                {selectedSet && <SetEditPage {...{ hostKey, selectedSet, updateSet }} />}
            </SideListFrame>
            {(messageWindowState.isOpen && messageWindowState.properties) && <MessageWindow {...messageWindowState.properties} />}
        </EditDialog>
    );
};

type SetEditDialogProps = Pick<SetEditDialogComponentProps, "hostKey" | "onCloseEdit">;
type StateProps = Pick<SetEditDialogComponentProps, "displayedSetIndex" | "sets" | "newSetText">;

const createMapStateToProps: (state: IAppState, props: SetEditDialogProps) => MapStateToProps<StateProps, SetEditDialogProps, IAppState> = (_state, { hostKey }) => {
    const getOrderedSets = createGetOrderedSets();

    return (state) => ({
        displayedSetIndex: getSelectedSetIndex(state, hostKey),
        sets: getOrderedSets(state, hostKey),
        newSetText: getPrimaryText(state, LOC_NEW_SET) ?? "Nová sada",
    });
};

type DispatchProps = Pick<SetEditDialogComponentProps, "updateSets">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, SetEditDialogProps> = (dispatch, { hostKey }) => ({
    updateSets: (newSets) => dispatch(createUpdateSetsAction(hostKey, newSets)),
});

const mergeProps: MergeProps<StateProps, DispatchProps, SetEditDialogProps, SetEditDialogComponentProps> = (
    stateProps,
    dispatchProps,
    ownProps,
) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    uiWorkflow: getUiWorkflowMutableObject(ownProps.hostKey),
});

export const SetEditDialog = connect(createMapStateToProps, mapDispatchToProps, mergeProps)(SetEditDialogComponent);
